import { FileIcon } from '@/assets/icons'
import React, { useRef } from 'react'
import toast from 'react-hot-toast'
import { FileInput } from '@components/Field'
import {
	AddButton,
	EmptyAttachments,
	RemoveButton,
	SectionContainer,
	SectionHeader,
	SectionLabel,
	ThumbnailDocIcon,
	ThumbnailGrid,
	ThumbnailItem,
} from './AttachmentSection.styles'
import { iconSizes } from '@/theme/tokens'

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic']
const ACCEPTED_TYPES = [
	...IMAGE_MIME_TYPES,
	'application/pdf',
	'application/msword',
	'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	'application/vnd.ms-excel',
	'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	'application/vnd.ms-powerpoint',
	'application/vnd.openxmlformats-officedocument.presentationml.presentation',
	'text/plain',
	'text/csv',
].join(',')

const MAX_ATTACHMENTS = 5

interface PendingAttachmentPickerProps {
	files: File[]
	onChange: (files: File[]) => void
}

function getFileExtension(fileName: string): string {
	const parts = fileName.split('.')
	return parts.length > 1 ? parts[parts.length - 1] : ''
}

const PendingAttachmentPicker: React.FC<PendingAttachmentPickerProps> = ({ files, onChange }) => {
	const fileInputRef = useRef<HTMLInputElement>(null)
	const previewUrls = useRef<Map<File, string>>(new Map())

	const remaining = MAX_ATTACHMENTS - files.length

	const handleAddClick = () => {
		fileInputRef.current?.click()
	}

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selected = e.target.files
		if (!selected || selected.length === 0) return

		const newFiles = Array.from(selected)
		if (newFiles.length > remaining) {
			toast.error(`You can add ${remaining} more attachment${remaining !== 1 ? 's' : ''}.`)
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}

		onChange([...files, ...newFiles])
		if (fileInputRef.current) fileInputRef.current.value = ''
	}

	const handleRemove = (index: number) => {
		const file = files[index]
		const url = previewUrls.current.get(file)
		if (url) {
			URL.revokeObjectURL(url)
			previewUrls.current.delete(file)
		}
		onChange(files.filter((_, i) => i !== index))
	}

	const getPreviewUrl = (file: File): string => {
		if (!previewUrls.current.has(file)) {
			previewUrls.current.set(file, URL.createObjectURL(file))
		}
		return previewUrls.current.get(file)!
	}

	return (
		<SectionContainer>
			<SectionHeader>
				<SectionLabel>
					<FileIcon />
					Attachments {files.length > 0 && `(${files.length}/${MAX_ATTACHMENTS})`}
				</SectionLabel>
				{files.length > 0 && remaining > 0 && (
					<AddButton onClick={handleAddClick} type="button">
						+ Add
					</AddButton>
				)}
			</SectionHeader>

			<FileInput
				ref={fileInputRef}
				multiple
				accept={ACCEPTED_TYPES}
				onChange={handleFileChange}
			/>

			{files.length === 0 ? (
				<EmptyAttachments type="button" onClick={handleAddClick}>
					<FileIcon sx={{ fontSize: iconSizes.xs }} />
					Add attachments
				</EmptyAttachments>
			) : (
				<ThumbnailGrid>
					{files.map((file, index) => (
						<ThumbnailItem key={`${file.name}-${index}`}>
							{IMAGE_MIME_TYPES.includes(file.type) ? (
								<img src={getPreviewUrl(file)} alt={file.name} />
							) : (
								<ThumbnailDocIcon>
									<FileIcon sx={{ fontSize: iconSizes.lg }} />
									<span>{getFileExtension(file.name)}</span>
								</ThumbnailDocIcon>
							)}
							<RemoveButton type="button" onClick={() => handleRemove(index)} title="Remove">
								✕
							</RemoveButton>
						</ThumbnailItem>
					))}
				</ThumbnailGrid>
			)}
		</SectionContainer>
	)
}

export default PendingAttachmentPicker
